import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';

/**
 * Employee LMS
 *
 * This screen is not a real page with data. It only forwards the user to the
 * external Employee LMS website, which has to be opened in a NEW browser tab so
 * the admin panel stays open in the current tab.
 *
 * Flow:
 * 1. The user clicks "Employee LMS" in the sidebar and lands here.
 * 2. As soon as this screen loads we try to open the LMS in a new tab.
 * 3. Some browsers block tabs that are opened automatically (pop-up blocker).
 *    In that case we show a button so the user can open it with a real click,
 *    which is never blocked.
 */
const EmployeeLms = () => {
  // Address of the external Employee LMS website.
  const lmsURL = 'http://employee.intervaledu.com';

  // True when the browser refused to open the new tab automatically.
  const [popupBlocked, setPopupBlocked] = useState(false);

  // React can mount a component twice in development (StrictMode). This flag
  // makes sure we only try to open the LMS tab once, not twice.
  const alreadyOpenedRef = useRef(false);

  /**
   * Opens the LMS in a new browser tab.
   * "noopener,noreferrer" is a security setting: it stops the new tab from
   * being able to control the admin panel tab.
   */
  const openLms = () => {
    const newTab = window.open(lmsURL, '_blank', 'noopener,noreferrer');

    // window.open returns null (or an unusable window) when a pop-up blocker
    // stopped it. Then we ask the user to click the button instead.
    if (!newTab) {
      setPopupBlocked(true);
      return;
    }

    setPopupBlocked(false);
  };

  // Try to open the LMS automatically the first time this screen is shown.
  useEffect(() => {
    if (alreadyOpenedRef.current) return;
    alreadyOpenedRef.current = true;

    openLms();
  }, []);

  return (
    // GlobalWrapper gives this screen the same page title, breadcrumb and
    // entry animation that every other screen in the admin panel uses.
    <GlobalWrapper title={'Employee LMS'}>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            maxWidth: 520,
            width: '100%',
            p: 4,
            textAlign: 'center',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
          }}
        >
          {/* Row 1: one short line of text */}
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, whiteSpace: 'nowrap' }}>
            {popupBlocked ? 'Employee LMS opened in a new tab, Click again to open in new tab.' : 'Employee LMS opened in a new tab.'}
          </Typography>

          {/* Row 2: the button */}
          <Button variant="contained" color="primary" sx={{ color: 'white' ,boxShadow: 'none' }} startIcon={<OpenInNewIcon />} onClick={openLms}>
            Open Employee LMS
          </Button>
        </Paper>
      </Box>
    </GlobalWrapper>
  );
};

export default EmployeeLms;
